import {Color} from '@benchling/nucleus-ui';
import {debounce} from 'ts-debounce';

import {
  readingFrameStartIndex,
  frameIsVisible,
  getFrameStarts,
  getReadingFrameAminoAcids,
  getReadingFrameAAMap,
  AminoAcidsByFrameName,
  ReadingFrameAAMap,
} from 'biojs/ReadingFrameUtil';
import * as BiojsTranslation from 'biojs/Translation';
import {MenuChoicesOptions} from 'components/SelectionContextMenuHelpers';
import {ALIGNMENT_SCROLLING_PERF_INTERVAL, ReadingFrameName} from 'constants/AlignmentConstants';
import * as MapsConstants from 'constants/MapsConstants';
import {Translations} from 'molbio/maps/linear/LinearLayout';
import TranslationLayeredView, {TranslationsViewOptions} from 'molbio/maps/linear/TranslationLayeredView';
import {AlignmentMapState} from 'molbio/maps/MapStates';
import Range from 'molbio/maps/selectables/Range';
import {AminoAcid} from 'molbio/MolBioConstants';
import SequenceAlignmentTabState from 'molbio/tabstates/SequenceAlignmentTabState';
import ComponentTranslation from 'molbio/translations/backbone/Translation';
import * as Perf from 'performance/Perf';
import {READING_FRAMES} from 'settings/visibility-options/VisibilityOptions';

interface ReadingFramesViewOptions extends TranslationsViewOptions {
  range: Range;
}

/*
View for rendering reading frames on an alignment. Its model is an AlignmentMapState.
 */
class ReadingFramesView extends TranslationLayeredView<ComponentTranslation> {
  performanceId!: Perf.PerfId;
  visibleRange!: Range;
  override model!: AlignmentMapState;
  override tabstate!: SequenceAlignmentTabState;
  masterAminoAcidMap!: ReadingFrameAAMap;

  static override initClass(): void {
    this.prototype.className = 'readingFrames';
    this.prototype.setting = READING_FRAMES.name;
  }

  override initialize(options: ReadingFramesViewOptions): void {
    super.initialize(options);
    this.performanceId = Perf.start(
      'drawAlignmentReadingFrames',
      {enabled: false},
      {
        interval: ALIGNMENT_SCROLLING_PERF_INTERVAL,
        aggregations: [Perf.Aggregation.MEDIAN, Perf.Aggregation.MAX],
      },
      {}
    );
    this.layout = this.model.get('layout').Translations;
    this.visibleRange = options.range;
  }

  override registerListeners(): void {
    super.registerListeners();
    this.listenTo(this.visibleRange, 'change:start change:end', debounce(this.update.bind(this), 100));
    this.listenToTrim(this.model);
    if (!this.model.isMasterSequence()) {
      this.listenToTrim(this.tabstate.getMapStateForMasterSequence());
    }
  }

  // Noop to improve vertical scrolling performance, as there are no tooltips in ReadingFramesView to remove
  override killTooltips(): void {}

  override getRenderStart(): number {
    // Render a bit more to the left than what's shown to prevent the user
    // seeing AAs jumping on screen when scrolling
    return Math.max(Math.floor(this.visibleRange.start()) - 100, this.slice.start());
  }

  override getRenderEnd(): number {
    // Render a bit more to the right than what's shown to prevent the user
    // seeing AAs jumping on screen when scrolling
    return Math.min(Math.ceil(this.visibleRange.end()) + 100, this.slice.end());
  }

  override aminoAcidFillColor(aminoAcid: AminoAcid, componentTranslation: ComponentTranslation): string {
    const acidLetter = aminoAcid.b;
    const frame: ReadingFrameName = componentTranslation.get('frameName');
    const templateAA = this.masterAminoAcidMap[frame][aminoAcid.indexStart];
    if (acidLetter === '?') {
      return Color.ORANGE_400;
    }
    if (!templateAA || templateAA !== acidLetter) {
      // Color amino acids red if they're out of alignment with the master or if they are a different acid
      return Color.RED_300;
    }

    return Color.GRAY_200;
  }

  override aminoAcidGlyphColor(): string {
    return Color.BLACK;
  }

  listenToTrim(mapState: AlignmentMapState): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(mapState.get('sequence').get('_sequence'), 'updateTrim', this.update);
  }

  override strokeColor(): string {
    return Color.GRAY_300;
  }

  getTrimRange(mapState: AlignmentMapState): {trimStart: number; trimEnd: number} {
    const trim = mapState.get('sequence').get('trim');
    if (!trim) {
      return {trimStart: 0, trimEnd: mapState.get('sequence').bases().length};
    } else {
      return {trimStart: trim.start(), trimEnd: trim.end()};
    }
  }

  override update(trigger?: boolean): void {
    Perf.markStart(this.performanceId);

    if (this.isShown()) {
      // Create map of indices to acid letters to use later in determining acid color
      // Called once per render/update to prevent recalculating for each translation
      const masterMapState = this.tabstate.getMapStateForMasterSequence();
      const masterGapSequenceWrapper = masterMapState.get('sequence');
      const {trimStart, trimEnd} = this.getTrimRange(masterMapState);
      this.masterAminoAcidMap = getReadingFrameAAMap(masterGapSequenceWrapper, trimStart, trimEnd);
    }
    super.update(trigger);

    Perf.markEnd(this.performanceId);
  }

  override getData(): Array<ComponentTranslation> {
    /*
    Creates ComponentTranslation models on the fly based on the current trims
    of the current sequence and the master sequence. Makes one translation per reading frame.
     */
    const frameVisibilitySettings = this.model
      .get('visibilityOptions')
      .getOption(this.setting)!
      .get('settings');

    const masterMapState = this.tabstate.getMapStateForMasterSequence();
    const masterGapSequenceWrapper = masterMapState.get('sequence');
    const {trimStart, trimEnd} = this.getTrimRange(this.model);
    const masterFrameStarts = getFrameStarts(
      masterGapSequenceWrapper.bases(),
      this.getTrimRange(masterMapState).trimStart
    );

    // Pre-calculate amino acids for all reading frames that have masterFrameStarts
    // Since they're used in both the filter and the map
    // @ts-expect-error
    const aminoAcidsByFrameName: AminoAcidsByFrameName = {};
    for (const frameName of Object.keys(masterFrameStarts)) {
      // @ts-expect-error
      aminoAcidsByFrameName[frameName] = getReadingFrameAminoAcids(
        masterGapSequenceWrapper,
        // @ts-expect-error
        masterFrameStarts[frameName],
        trimStart,
        trimEnd
      );
    }

    const currentBases = this.model.get('sequence').bases();
    return frameVisibilitySettings
      .filter((visibilitySetting) =>
        frameIsVisible(masterFrameStarts, aminoAcidsByFrameName, visibilitySetting, trimStart, currentBases)
      )
      .map((visibilitySetting, i) => {
        const frameName: ReadingFrameName = visibilitySetting.get('name');
        const masterAminoAcids = aminoAcidsByFrameName[frameName];
        const translationOptions = {
          start: readingFrameStartIndex(masterAminoAcids, trimStart, currentBases),
          end: trimEnd,
          strand: 1,
          fromSubPart: false,
        };
        return new ComponentTranslation({
          _translation: new BiojsTranslation.Model(translationOptions),
          frameName,
          [MapsConstants.LINEAR_LAYER_FULL]: i, // ensures the frames stack instead of overlapping
        });
      });
  }

  override getLayout(): Translations {
    return this.model.get('layout').Translations;
  }

  override isShown(): boolean {
    return this.shouldShow();
  }

  override contextMenuOptions(): Array<string> {
    return [];
  }

  override contextMenuData(): MenuChoicesOptions {
    return {};
  }
}

ReadingFramesView.initClass();

export default ReadingFramesView;
